import React, { useState } from 'react';
import { WizardContext } from '../wizard/wizardProvider';

interface CharterPageProps {
    showWizardLinks: boolean;
    setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CharterPage: React.FC<CharterPageProps> = ({ showWizardLinks, setDialogOpen }) => {
    const wizard = React.useContext(WizardContext);
    const [selectedHelpPage, setSelectedHelpPage] = useState(showWizardLinks ? "wizard" : "creating");

    function getContent(name: string): JSX.Element {
        switch (name) {
            case "wizard":
                return <Wizards wizard={wizard} setDialogOpen={setDialogOpen} />
            case "creating":
                return <CreatingCharter />
            case "team":
                return <BuildingTeam />
            case "plan":
                return <BuildingPlan />
            case "submit":
                return <SubmitCharter />
            case "approval":
                return <SponsorApproval />
            case "change":
                return <ProjectChangeRequest />
            case "accept":
                return <AcceptingChangeRequests />
            default:
                return <CreatingCharter />
        }
    }

    return (
        <div className="flex flex-row w-full pt-4">
            <div className="flex flex-col min-w-[200px] mr-2">
                <ul className="text-center">
                    {showWizardLinks && (
                        <li className={(selectedHelpPage == "wizard" ? "bg-table-subheader " : "bg-table-header ") + "rounded-tl-md p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("wizard")}>Wizards</li>
                    )}
                    <li className={(selectedHelpPage == "creating" ? "bg-table-subheader " : "bg-table-header ") + "rounded-tl-md p-2 bg-table-header hover:cursor-pointer "} onClick={() => setSelectedHelpPage("creating")}>Creating a Charter</li>
                    <li className={(selectedHelpPage == "team" ? "bg-table-subheader " : "bg-table-header ") + "p-2 hover:cursor-pointer "} onClick={() => setSelectedHelpPage("team")}>Building a Team</li>
                    <li className={(selectedHelpPage == "plan" ? "bg-table-subheader " : "bg-table-header ") + "p-2 hover:cursor-pointer "} onClick={() => setSelectedHelpPage("plan")}>Building a Plan</li>
                    <li className={(selectedHelpPage == "submit" ? "bg-table-subheader " : "bg-table-header ") + "p-2 hover:cursor-pointer "} onClick={() => setSelectedHelpPage("submit")}>Submit for Approval</li>
                    <li className={(selectedHelpPage == "approval" ? "bg-table-subheader " : "bg-table-header ") + "p-2 hover:cursor-pointer "} onClick={() => setSelectedHelpPage("approval")}>Sponsor Approval</li>
                    <li className={(selectedHelpPage == "change" ? "bg-table-subheader " : "bg-table-header ") + "p-2 hover:cursor-pointer "} onClick={() => setSelectedHelpPage("change")}>Project Change Request</li>
                    <li className={(selectedHelpPage == "accept" ? "bg-table-subheader " : "bg-table-header ") + "rounded-bl-md p-2 hover:cursor-pointer "} onClick={() => setSelectedHelpPage("accept")}>Approve Change Request</li>
                </ul>
            </div>
            <div className="flex flex-col h-[800px] p-2 overflow-y-auto scrollbar">{getContent(selectedHelpPage)}</div>
        </div>
    );
};

export default CharterPage;

interface WizardsProps {
    wizard: React.ContextType<typeof WizardContext>;
    setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Wizards: React.FC<WizardsProps> = ({ wizard, setDialogOpen }) => {
    return (
        <>
            <button onClick={() => { wizard.startGuidedTour(); setDialogOpen?.(false); }} className="ml-2 px-2 py-1 bg-blue-500 text-white rounded-md w-40">Start Guided Tour</button>
            <p>
                The Guided Tour Wizard simply explains the various sections of the Charter page and what information is required.
            </p>
        </>
    );
};

const CreatingCharter: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Creating a Charter</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/Creating_new_project.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        A new charter is created automatically when you create a new project.
                    </p>
                    <br />
                    <p>
                        Create a new project via the project selection dropdown menu.
                    </p>
                    <br />
                    <p>
                        You will be redirected to the charter page. Lock the charter page for editing by clicking the page lock toggle in the top right corner.
                    </p>
                    <br />
                    <p>
                        When you edit a field, it will be saved automatically when you leave the field, which will be indicated with a message in the taskbar
                        at the bottom right corner of the screen.
                    </p>
                </div>
            </div>
        </>
    );
}

const BuildingTeam: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Building a Team</h1>
            <div className="flex flex-row items-start">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/Charter_Team_setup.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        To build your team for the project, click on relevent position and you will be presented with a list of all users registered on the system.
                        Select the users you would like to add to the role by checking the box to the left of the user name. Once you have selected the users you would like
                        to add into that role, click the arrow button in the middle, and they will be removed from the options list and added to the list for the role.
                    </p>
                    <br />
                    <p>
                        You can remove users from a role individually by clicking on the ellipsis on the right hand side of the row and selecting 'remove'. You can
                        remove multiple users at once by selected the users you wish to remove using the left hand check boxes, clicking the ellipses next to
                        the selected count underneath the table and selecting 'remove'.
                    </p>
                    <br />
                    <p>
                        Instead of closing the popup and clicking the next box to open the popup again, you have the option to select from the dropdown which role you would like
                        to edit.
                    </p>
                    <br />
                    <p>
                        For speed of entry, you also have the option to edit the entire team in one go. The form then behaves exactly as the Team Builder Page
                        and the Team builder popup on the Plan Builder page.
                    </p>
                </div>
            </div>
        </>
    );
}

const BuildingPlan: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Building a Plan</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/Open_plan_from_charter.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        Clicking on the 'High Level Timeline' table will navigate you to the Plan page where you can build your plan.
                    </p>
                    <br />
                    <p>
                        Click here for more information on how to use the Plan Builder
                    </p>
                </div>
            </div>
        </>
    );
}

const SubmitCharter: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Submit Charter for Approval</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/charter_submit_for_approval.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        Once you have finished building your filling out your charter, building your team and your plan, the next step is to submit the plan for
                        approval by the projects sponsor
                    </p>
                    <br />
                    <p>
                        Select the 'Deliverables' tab. Below the lists of deliverables is the option to submit for approval.
                    </p>
                    <br />
                    <p>
                        Once submitted the button will be disabled and the text will change to say it has been submitted. On the 'Charter' tab, you will see
                        the 'Charter Status' nw says 'Submitted for Approval'
                    </p>
                    <br />
                    <p>
                        To undo the submission, simply edit one of the fields before the charter is approved. This will reset the 'Charter Status' to 'Unapproved'
                    </p>
                </div>
            </div>
        </>
    );
}

const SponsorApproval: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Sponsor Approval</h1>
            <div className="flex flex-row items-start">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/charter_sponsor_approval.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        When the Charter is submitted for approval, the project sponsor(s) will recieve a notification that the charter has been submitted.
                        If the charter is edited before the sponsor approves the charter, the notification will be removed.
                    </p>
                    <br />
                    <p>
                        Clicking on the header of the notifcation will navigate to the charter page of the project in question
                    </p>
                    <br />
                    <p>
                        On the 'Deliverbles' tab, the sponsor will see a button to approve the charter. If they do not agree and would like to make a change,
                        they can edit the field in question and the submission for approval will be removed and the project manager will be notified that the charter
                        has been udated. They will then have to resubmit the charter for approval, assuming they agree with the change.
                    </p>
                    <br />
                    <p>
                        If the sponsor approves the charter, the sponsors name and the current date is added as a digital signature.
                    </p>
                </div>
            </div>
            <div className="flex flex-row mt-4">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/charter_after_sponsor_approval.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        When the Charter is approved, the project manager is notified. Clicking on the notification header navigates to the charter.
                    </p>
                    <br />
                    <p>
                        The Charter is now locked down and can no longer be edited. The 'Charter Status' is now 'Approved'.
                    </p>
                    <br />
                    <p>
                        Once approved, the charter can only be edited by submitting a 'Project Change Request'.
                    </p>
                </div>
            </div>
            <div className="flex flex-row mt-4">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/Charter_approved_report.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        When the Charter is approved a report is generated as a record. This can be found on the 'Reports' page.
                    </p>
                    <br />
                    <p>
                        Every time the charter is changed after the initial approval another report is generated. These subsequent reports will have an extra
                        page which shows the project change request that lead to the change.
                    </p>
                </div>
            </div>
        </>
    );
}

const ProjectChangeRequest: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Project Change Request</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/Charter_project_change_request.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        If you wish to make a change to the charter after it has been approved you must submit a 'Project Change Request'
                    </p>
                    <br />
                    <p>
                        Select the 'Project Change Reuqest' tab on the Charter Page. Project Change Requests are created by addin a row to the Project Change Control table.
                    </p>
                </div>
            </div>
        </>
    );
}

const AcceptingChangeRequests: React.FC = () => {
    return (
        <>
            <h1 className="text-2xl font-bold mb-2">Approve a Project Change Request</h1>
            <div className="flex flex-row">
                <video width="960" height="720" controls>
                    <source src="/videos/help/charter/Charter_accept_projectChangeRequest.mp4" type="video/mp4" />
                </video>
                <div className="p-2">
                    <p>
                        Project Change Requests are accepted by clicking the ellipses on the row in the Project Change Control table and selecting 'Approve'
                    </p>
                    <br />
                    <p>
                        Once the Change Request has been approved the Charter will be unlocked. Making a change will then change the 'Charter Status' to 'Unapproved'
                        and allow the Charter to be re-submitted for approval.
                    </p>
                </div>
            </div>
        </>
    );
}